import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';

dayjs.extend(relativeTime);
dayjs.locale('fr');


/**
 * Formate une date en chaîne lisible selon la locale 'fr-FR'.
 * @param {string} dateString - La date au format ISO ou une chaîne spéciale comme '0000-00-00'.
 * @returns {string} La date formatée ou 'N/A' si invalide.
 */
export const formatDate = (dateString) => {
    if (dateString === '0000-00-00' || !dateString) {
        return '';
    }

    const date = new Date(dateString);
    if (isNaN(date)) {
        return '';
    }

    return date.toLocaleDateString('fr-FR');
};

/**
 * Formate des secondes en une chaîne de type "HHhMM".
 * @param {number|string} seconds - Le nombre de secondes ou une chaîne qui peut être convertie en nombre.
 * @returns {string} L'heure formatée ou 'N/A' si invalide.
 */
export const formatHeure = (seconds) => {
    if (!seconds || isNaN(seconds)) {
        return '';
    }

    const totalSeconds = Number(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    return `${String(hours).padStart(2, '0')}h${String(minutes).padStart(2, '0')}`;
};

/**
 * Formate un montant en monnaie avec la locale 'fr-FR' et l'Euro (€) par défaut.
 * Supprime les décimales si le montant est un entier et ajoute un espace pour les milliers.
 * @param {number|string} amount - Le montant à formater.
 * @param {string} [currency='EUR'] - La devise à utiliser (par défaut 'EUR').
 * @returns {string} Le montant formaté ou '0 €' si invalide.
 */
export const formatCurrency = (amount, currency = 'EUR') => {
    const parsedAmount = Number(amount);
    if (isNaN(parsedAmount)) return '0 €';

    const hasDecimals = !Number.isInteger(parsedAmount);

    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency,
        useGrouping: true, // <-- active l'espace pour les milliers
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(parsedAmount);
};

/**
 * Met la première lettre d'une chaîne en majuscule, en étant safe pour undefined/null/numériques.
 * @param {*} input - La valeur à capitaliser.
 * @returns {string} La chaîne avec la première lettre en majuscule ou chaîne vide si invalide.
 */
export const capitalize = (input) => {
    if (typeof input !== 'string' || input.length === 0) {
        return '';
    }
    return input.charAt(0).toUpperCase() + input.slice(1);
};

export const fmtBool = (b) => (b ? "Oui" : "Non");

/**
 * Capitalise chaque mot d'une chaîne (utile pour prénoms et noms).
 * Gère undefined, null ou valeurs non chaînes.
 * @param {*} input - La chaîne à transformer.
 * @returns {string} La chaîne avec chaque mot capitalisé ou chaîne vide.
 */
export const capitalizeEach = (input) => {
    if (typeof input !== 'string' || input.trim().length === 0) {
        return '';
    }
    return input
        .trim()
        .split(/\s+/)
        .map(capitalize)
        .join(' ');
};

/**
 * Formate une date et une heure en une chaîne lisible.
 * @param {string} dateStr - La date au format ISO.
 * @param {number} secondsSinceMidnight - Le nombre de secondes depuis minuit
 * @return {string} La date formatée ou une chaîne vide si invalide.
 */

export const formatMessageDate = (dateStr, secondsSinceMidnight) => {
    if (!dateStr || secondsSinceMidnight === undefined) return '';

    const fullDate = dayjs(dateStr).startOf('day').add(secondsSinceMidnight, 'second');
    const now = dayjs();

    const diffInHours = now.diff(fullDate, 'hour');
    const isToday = fullDate.isSame(now, 'day');
    const isYesterday = fullDate.isSame(now.subtract(1, 'day'), 'day');

    if (isToday && diffInHours < 24) {
        return fullDate.fromNow();
    }

    if (isYesterday) {
        return 'Hier';
    }

    return fullDate.format('DD/MM/YYYY');
};

export const formatHtmlToText = (html) => {
    if (!html) return '';

    let text = html;

    // Handle <br> and <p> as newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');

    // Bold → **text**
    text = text.replace(/<b>(.*?)<\/b>/gi, '**$1**');
    text = text.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');

    // Italic → *text*
    text = text.replace(/<i>(.*?)<\/i>/gi, '*$1*');
    text = text.replace(/<em>(.*?)<\/em>/gi, '*$1*');

    // Underline → _text_
    text = text.replace(/<u>(.*?)<\/u>/gi, '_$1_');

    // Remove other tags but keep their text content
    text = text.replace(/<[^>]+>/g, '');

    // Decode HTML entities (like &nbsp;, &amp;)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || '';
};


export const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Lecture du fichier échouée"));
        reader.onload = () => {
            const result = reader.result || "";
            const [prefix, base64] = String(result).split("base64,");
            const mime = (prefix.match(/^data:(.*);/) || [])[1] || file.type || "";
            resolve({
                name: file.name,
                size: file.size,
                mime,
                base64: base64 || "",
            });
        };
        reader.readAsDataURL(file);
    });